import { useSignedInUser } from "../../features/auth/hooks/useSignedInUser";
import { useAccessLapse } from "../../features/billing/hooks/useAccessLapse";
import { useCurrentSubscription } from "../../features/billing/hooks/useCurrentSubscription";
import { PRODUCT_NAME } from "../../features/billing/data/subscriptionPlans";
import type { AccessLapse } from "../../features/billing/accessLapse";
import type { Trial } from "../../features/billing/types/subscriptions.types";

export interface AccountSummary {
  /** Their own name, or the address where Clerk holds no name. */
  displayName: string;
  /** Their profile picture, or `""` — the caller draws the initial instead. */
  imageUrl: string;
  /** The plan they are on, or the address they are signed in as. `""` for an
   *  account known only by its address, where repeating it says nothing. */
  accountLine: string;
  /** Whether that line names a plan, which is what it is coloured by. */
  isSubscribed: boolean;
  /** The trial to draw, or `null` while the billing read is still out. */
  trial: Trial | null;
  /** What has run out, where it can be told — see below. */
  lapse: AccessLapse;
}

/**
 * Who is signed in and what they are on, as the two navigations both draw it:
 * the account block at the foot of the rail, and the head of the phone's
 * account menu.
 *
 * One hook rather than the same twenty lines in each, because the two are
 * meant to be the same face — a phone that named the plan differently from the
 * rail would be describing two accounts.
 */
export const useAccountSummary = (): AccountSummary => {
  const { fullName, email, imageUrl } = useSignedInUser();
  const {
    trial,
    accessSource,
    status: subscriptionStatus,
  } = useCurrentSubscription();
  const { lapse } = useAccessLapse();

  // `useSignedInUser` walks Clerk's fields down to the email address; the
  // generic is for the moment before Clerk has loaded, and it is only ever a
  // flash — both navigations render behind `RequireAuth`, which has a session.
  const displayName = fullName || "your account";

  /**
   * The line under the name: the plan they are on, or — for everyone who is
   * not on one, the trial included — the address they are signed in as.
   *
   * Nothing is claimed until the read answers: the address is true whatever
   * billing comes back with, so it holds the line meanwhile rather than a plan
   * name that might be wrong. Where the account has no name at all, `fullName`
   * is already the address, and repeating it under itself says nothing — so the
   * line stands empty rather than doubling.
   */
  const isSubscribed =
    subscriptionStatus === "ready" && accessSource === "SUBSCRIPTION";
  const accountLine = isSubscribed
    ? PRODUCT_NAME
    : email && email !== displayName
    ? email
    : "";

  return {
    displayName,
    imageUrl,
    accountLine,
    isSubscribed,
    // A countdown drawn from a state we do not know yet is a claim about
    // somebody's account, so nothing is metered until the read has answered.
    trial: subscriptionStatus === "ready" ? trial : null,
    /**
     * What has run out, which is what the card at the foot of the rail says
     * instead of a countdown — and which of the two endings it names.
     *
     * Handed straight through rather than flattened to a boolean here. It used
     * to be `trialEnded`, and the card drew nothing for anything else: a
     * practice between prepaid terms got no card at all, because the one it
     * would have shown was about a trial it finished a year ago. The answer to
     * that is the right words, not silence — the rail carries the only way back
     * onto a plan that it has. Worked out in one place for all three screens
     * that word it; see `accessLapse`.
     */
    lapse,
  };
};

export default useAccountSummary;
